import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Card, CardContent,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Alert, Chip, Stack, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type { ICategory } from '../../../shared/types.js';
import { EngineMode } from '../../../shared/types.js';

interface CategoryForm {
  internal_name: string;
  display_name: string;
  description: string;
  color: string;
  active: boolean;
}

const slugify = (s: string) =>
  s.toLowerCase()
    .replace(/[\s\-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const emptyForm: CategoryForm = {
  internal_name: '',
  display_name: '',
  description: '',
  color: '#1976d2',
  active: true,
};

export default function ConfigPlusCategoriesPage() {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<ICategory | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ categories: ICategory[] }>('/config/categories?engine_mode=rule_engine');
      if (res.success) setCategories(res.data.categories);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (cat: ICategory) => {
    setEditingId(cat.id);
    setForm({
      internal_name: cat.internal_name,
      display_name: cat.display_name,
      description: cat.description ?? '',
      color: cat.color ?? '#1976d2',
      active: cat.active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const res = await api.patch(`/config/categories/${editingId}`, { ...form, engine_mode: EngineMode.RULE_ENGINE });
        if (!res.success) {
          setError(res.error?.message ?? 'Speichern fehlgeschlagen');
          return;
        }
      } else {
        const res = await api.post('/config/categories', { ...form, engine_mode: EngineMode.RULE_ENGINE });
        if (!res.success) {
          setError(res.error?.message ?? 'Erstellen fehlgeschlagen');
          return;
        }
      }
      setDialogOpen(false);
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleToggleActive = async (cat: ICategory) => {
    try {
      await api.patch(`/config/categories/${cat.id}`, { active: !cat.active });
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/config/categories/${id}`);
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories
      .filter((cat) => {
        if (!showInactive && !cat.active) return false;
        if (!q) return true;
        return cat.display_name.toLowerCase().includes(q) || cat.internal_name.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.active === b.active) return a.display_name.localeCompare(b.display_name);
        return a.active ? -1 : 1;
      });
  }, [categories, search, showInactive]);

  if (loading) return <Typography>Lade Kategorien...</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Kategorien</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Toolbar: Suche + Toggle + Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Kategorie suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Inaktive einblenden</Typography>}
          sx={{ ml: 0 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' }, whiteSpace: 'nowrap' }}>
          Neue Kategorie
        </Button>
      </Box>

      {/* Kategorie-Liste */}
      <Stack spacing={2}>
        {filteredCategories.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {search ? 'Keine Treffer für die Suche.' : 'Keine Kategorien vorhanden. Erstelle die erste Kategorie.'}
          </Typography>
        ) : (
          filteredCategories.map((cat) => (
            <Card key={cat.id} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Farbkreis + Name */}
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: cat.color ?? '#999',
                      flexShrink: 0,
                      border: '1px solid rgba(0,0,0,0.15)',
                      opacity: cat.active ? 1 : 0.5,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, opacity: cat.active ? 1 : 0.5 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>{cat.display_name}</Typography>
                    {cat.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>{cat.description}</Typography>
                    )}
                  </Box>
                  {cat.slot !== null && (
                    <Chip label={`Slot ${cat.slot}`} size="small" sx={{ opacity: cat.active ? 1 : 0.5 }} />
                  )}
                  <Chip
                    label={cat.active ? 'Aktiv' : 'Inaktiv'}
                    size="small"
                    color={cat.active ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Switch
                    size="small"
                    checked={cat.active}
                    onChange={() => handleToggleActive(cat)}
                    title={cat.active ? 'Deaktivieren' : 'Aktivieren'}
                  />
                  <IconButton size="small" onClick={() => openEdit(cat)}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirmTarget(cat)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Kategoriename"
              value={form.display_name}
              fullWidth
              autoFocus
              onChange={(e) => {
                const display_name = e.target.value;
                if (!editingId) {
                  const base = slugify(display_name);
                  const existing = categories.map((c) => c.internal_name);
                  let candidate = base;
                  let i = 2;
                  while (existing.includes(candidate)) candidate = `${base}_${i++}`;
                  setForm({ ...form, display_name, internal_name: candidate });
                } else {
                  setForm({ ...form, display_name });
                }
              }}
              helperText={!editingId && form.display_name ? `Wird auf der Scorecard angezeigt` : 'Wird auf der Scorecard angezeigt'}
            />
            <TextField label="Beschreibung" value={form.description} fullWidth multiline rows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Farbe</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: form.color,
                    border: '2px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['#1976d2','#388e3c','#d32f2f','#f57c00','#7b1fa2','#0288d1','#c8e000','#455a64','#e91e63','#00796b'].map((preset) => (
                    <Box
                      key={preset}
                      onClick={() => setForm({ ...form, color: preset })}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: preset,
                        cursor: 'pointer',
                        border: form.color === preset ? '3px solid #000' : '2px solid rgba(0,0,0,0.15)',
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.2)' },
                      }}
                    />
                  ))}
                  <Box
                    component="label"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: '2px dashed #aaa',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      color: '#aaa',
                      '&:hover': { borderColor: '#555', color: '#555' },
                    }}
                    title="Eigene Farbe wählen"
                  >
                    +
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.display_name.trim()}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}>
            {editingId ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ──────────────────────── */}
      <Dialog open={!!deleteConfirmTarget} onClose={() => setDeleteConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Kategorie löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du die Kategorie <strong>{deleteConfirmTarget?.display_name}</strong> wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTarget(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteConfirmTarget) handleDelete(deleteConfirmTarget.id);
              setDeleteConfirmTarget(null);
            }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
